import React, {FC, ReactElement, useState} from 'react';
import type {ComboProps} from './Combo';
import styled from 'styled-components';
import {ComboDesigned} from './Combo';


type ComboEditorProps = {
    comboElement: any,
    comboSettings: any,
    comboOptions: any,
    label: string
}

type EditorProps = ComboProps & ComboEditorProps;

const ComboDesignedEditor = styled(ComboDesigned)`
&.comboWrapper {
      .inner_wrapper {
        cursor: pointer;
      }
      &:hover {
         outline: 0.5px solid #1676D0;
         background: none;
      }
      cursor:pointer;
       ${props => props.isDraggingOver && `
          background: rgba(22, 118, 208, 0.05);
          border: 1px solid #1676D0;
          cursor: move;
      `};
      ${props => props.isDragging && `
          cursor: move;
      `};
}
`;

const ComboEditor: FC<EditorProps> = ({...props}): ReactElement => {

    const useAppSelector = props.editorPlugins.useAppSelector;
    const useAppDispatch = props.editorPlugins.useAppDispatch;
    const selectActive = props.editorPlugins.selectActive;
    const setActiveType = props.editorPlugins.setActiveType;
    const setActiveProps = props.editorPlugins.setActiveProps;
    const selectActiveSet = props.editorPlugins.selectActiveSet;
    const selectActiveSettings = props.editorPlugins.selectActiveSettings;
    const setSettingsTitle = props.editorPlugins.setSettingsTitle;
    const updateData = props.editorPlugins.updateData;
    const trackStory = props.editorPlugins.trackStory;
    const Duplicate = props.editorPlugins.duplicateButton;
    const Delete = props.editorPlugins.deleteButton;
    const Add = props.editorPlugins.addButton;
    const Droppable = props.editorPlugins.droppable;
    const Draggable = props.editorPlugins.draggable;
    const scrollTo = props.editorPlugins.scrollTo;

    let Combo = props.comboElement;
    const activePath: any = useAppSelector((state: any) => state.editor.present.activePath);
    const globalData: any | null = useAppSelector((state: any) => state.editor.present.data);
    const openElementsLibrary = props.editorPlugins.openElementsLibrary;
    const dispatch = useAppDispatch();
    const index = props.index ? props.index : 0;
    const settings = props.comboSettings;
    const [ addComboTrigger, setAddComboTrigger ] = useState(false);

    const [isOptions, setIsOptions] = useState(false);

    function handleClick(event: any) {
        event.preventDefault();
        if (event.target === event.currentTarget || event.target.classList.contains('combo') || event.target.classList.contains('combo-inner') || event.target.classList.contains('tabElement') || event.target.classList.contains('narrator')) {
            dispatch(trackStory({event: 'click on combo', screen: 'editorv4', params: {'editorVersion':'V4'}}));
            return selectActiveCombo();
        }
    }

    function isComboActive() {
        if (!activePath) return false;
        if (!props.path) return false;
        return JSON.stringify(activePath) === JSON.stringify(props.path);
    }

    function selectActiveCombo() {
        setAddComboTrigger(false)
        dispatch(setActiveType('combo'));
        dispatch(setActiveProps(props.comboData.settings));
        settings ? dispatch(selectActiveSettings({settings})) : dispatch(selectActiveSettings(null));
        dispatch(trackStory({event: 'success active combo', screen: 'editorv4', params: {}}));
        dispatch(setSettingsTitle(props.comboData.name));
        return dispatch(selectActive(props.path));
    }

    function duplicateCombo() {
        dispatch(trackStory({event: 'click duplicate on active combo buttons', screen: 'editorv4', params: {}}));
        let obj = {
            ...props.comboData,
            name: props.comboData.name,
        };
        let combos = [...globalData.slides[activePath.slideIndex].combos];
        combos.splice(index + 1, 0, obj);
        let data = {
            path: activePath,
            data: combos,
            object: 'combo'
        };
        dispatch(updateData(data));
        dispatch(selectActive({...activePath, comboIndex: activePath.comboIndex + 1}));
        setTimeout(() => scrollToAddedCombo(activePath.slideIndex, activePath.comboIndex + 1), 500)
    }

    function scrollToAddedCombo(slideIndex: number, comboIndex: number) {
        let slides = document.querySelectorAll('.mainStory .slide');
        let slide = slides[ slideIndex ] as HTMLElement;
        if (!slide) return;
        let combos = slide.querySelectorAll('.inner-wrapper .comboWrapper.comboDragWrapper');
        const combo = combos[ comboIndex ] as HTMLElement;
        if (combo) scrollTo(combo, 'center');
    }

    function deleteCombo() {
        dispatch(trackStory({event: 'click delete on active combo buttons', screen: 'editorv4', params: {}}));
        let combos = [...globalData.slides[activePath.slideIndex].combos];
        combos.splice(index, 1);
        let data = {
            path: activePath,
            data: combos,
            object: 'combo'
        };
        dispatch(updateData(data));
        const comboIndex = activePath.comboIndex === combos.length ? activePath.comboIndex - 1 : activePath.comboIndex;
        dispatch(selectActive({ ...activePath, comboIndex: comboIndex }));
        setTimeout(() => scrollToAddedCombo(activePath.slideIndex, comboIndex), 500)
    }

    function addCombo(direction: string) {
        setAddComboTrigger(true);
        dispatch(trackStory({event: 'click add combo from main container', screen: 'editorv4', params: {}}));
        let path = {...props.path};
        if (path.comboIndex == null || path.comboIndex === undefined) return;
        if (direction === 'top') path.comboIndex = path.comboIndex - 1;
        let data = {
            path: path,
            type: 'combo'
        };
        dispatch(selectActiveSet(''));
        return dispatch(openElementsLibrary(data));
    }

    function setActiveSetting(activeSetting: any = null) {
        dispatch(selectActiveSet(activeSetting));
    }

    function optionAction(event: any, option: any) {
        event.stopPropagation();
        dispatch(trackStory({event: 'click ' + option.name + ' icon from active combo options', screen: 'editorv4', params: {}}));
        switch (option.action) {
            case 'editTabs':
            case 'editNarrator':
            case 'setActiveSetting':
                return setActiveSetting(option.settingTitle);
        }
    }

    function checkIfDisableCombo() {
        if(props.comboData.name === 'Tabs' || props.comboData.name === 'Narrator') return true;
    }

    return (
        <Draggable draggableId={props.path?.slideIndex + '-' + props.path?.comboIndex + '-' + props.path?.setIndex + '-' + props.path?.groupIndex + '-' + props.path?.elementIndex}
                   index={props.index} isDragDisabled={checkIfDisableCombo()} key={props.index}>
            {(provided: any, snapshot: any) => (
                <div className={'comboWrapper comboDragWrapper'} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                    data-anchor-target={props.comboData.settings.isAnchorActive ? props.comboData.settings.anchorName : ''}>
                    <div className="addCombo top" onClick={() =>  addCombo('top')}><Add/></div>
                    <Droppable droppableId={props.path?.slideIndex + '-' + props.path?.comboIndex} type={'set'} direction={props.comboData.name === 'Array' ? 'horizontal' : 'vertical'}>
                        {(provided: any, snapshot: any) => (
                            <ComboDesignedEditor
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={'comboWrapper ' + (isComboActive() && !addComboTrigger ? 'activeMode' : '') + (activePath.comboIndex === props.path?.comboIndex ? ' currentCombo' : '')}
                                onClick={(event: any) => {
                                    handleClick(event);
                                }}
                                verticalAlignment={props.comboData.settings.verticalAlignment}
                                horizontalAlignment={props.comboData.settings.horizontalAlignment} backgroundColor={props.comboData.settings.backgroundColor}
                                borderColor={props.comboData.settings.borderColor} borderWidth={props.comboData.settings.borderWidth} round={props.comboData.settings.round}
                                minHeight={props.comboData.settings.minHeight} maxWidth={props.comboData.settings.maxWidth} isCustomHeight={props.comboData.settings.isCustomHeight}
                                isCustomWidth={props.comboData.settings.isCustomWidth} isDraggingOver={snapshot.isDraggingOver} isDragging={snapshot.isDragging} data-direction={props.comboData.name === 'Array' ? 'horizontal' : 'vertical'}>
                                <div className={'inner_wrapper combo-inner'}>
                                    <Combo {...props.comboData} editorPlugins={props.editorPlugins} index={props.index} path={props.path} isEmptySlide={props.isEmptySlide}/>
                                </div>
                                <div className="options">
                                    <div className="left">
                                        {
                                            props.label && <div className="elementName">{props.label}</div>
                                        }
                                        {
                                            props.comboOptions && <div className={'optionsWrapper ' + (!isOptions ? 'noOptions' : '')}>
                                                {
                                                    props.comboOptions.length && props.comboOptions.map((option: any, index: number) => {
                                                        let passCondition = props.comboData.name === option.condition;
                                                        if (!option.condition || (option.condition && passCondition)) {
                                                            setIsOptions(true);
                                                            return (<div key={index} className={'option ' + option.name} onClick={(event) => optionAction(event, option)}>
                                                                    <img src={option.icon} alt={option.title}/>{option.title}
                                                                </div>
                                                            );
                                                        }
                                                    })
                                                }
                                            </div>
                                        }
                                    </div>
                                    <div className="right">
                                        <div className={'permanentOptions'}>
                                            <div className={'option duplicate'} onClick={() => {
                                                duplicateCombo();
                                            }}><Duplicate isOption={true}/></div>
                                            <div className={'option delete'} onClick={() => {
                                                deleteCombo();
                                            }}><Delete isOption={true}/></div>
                                        </div>
                                    </div>
                                </div>
                                {provided.placeholder}
                            </ComboDesignedEditor>
                        )}
                    </Droppable>
                    <div className="addCombo" onClick={() => addCombo('bottom')}><Add/></div>
                </div>
            )}
        </Draggable>
    );
};


export default ComboEditor;
